import React, { Component } from 'react'
import PropTypes from 'prop-types'
import './ErrorBoundary.scss'

/**
 * ErrorBoundary - React Error Boundary component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI.
 *
 * @param {React.ReactNode} children - Child components to wrap
 * @param {React.ReactNode} fallback - Custom fallback UI
 * @param {Function} onError - Error callback
 * @param {Function} onRetry - Retry callback
 * @param {string} title - Error title
 * @param {string} message - Error message
 * @param {boolean} showDetails - Show error details in development
 */
class ErrorBoundary extends Component {
	constructor(props) {
		super(props)
		this.state = {
			hasError: false,
			error: null,
			errorInfo: null
		}
	}

	static getDerivedStateFromError(error) {
		// Update state so the next render will show the fallback UI
		return { hasError: true, error }
	}

	componentDidCatch(error, errorInfo) {
		// Log the error to console
		console.error('ErrorBoundary caught an error:', error, errorInfo)

		// Store error info for display
		this.setState({ errorInfo })

		// Call onError callback if provided
		if (this.props.onError) {
			this.props.onError(error, errorInfo)
		}
	}

	handleRetry = () => {
		// Reset the error state
		this.setState({
			hasError: false,
			error: null,
			errorInfo: null
		})

		// Call onRetry callback if provided
		if (this.props.onRetry) {
			this.props.onRetry()
		}
	}

	render() {
		const {
			children,
			fallback,
			title = 'Something went wrong',
			message = 'An unexpected error occurred. Please try again.',
			showDetails = process.env.NODE_ENV === 'development'
		} = this.props

		const { hasError, error, errorInfo } = this.state

		if (hasError) {
			// Use custom fallback if provided
			if (fallback) {
				return typeof fallback === 'function'
					? fallback({ error, errorInfo, retry: this.handleRetry })
					: fallback
			}

			// Default error UI
			return (
				<div className="error-boundary" role="alert" aria-live="assertive">
					<div className="error-boundary__content">
						<div className="error-boundary__icon">
							<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
								<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
							</svg>
						</div>

						<h2 className="error-boundary__title">{title}</h2>
						<p className="error-boundary__message">{message}</p>

						{showDetails && error && (
							<details className="error-boundary__details">
								<summary>Error Details</summary>
								<div className="error-boundary__stack">
									<p className="error-boundary__error-name">{error.name}: {error.message}</p>
									{errorInfo?.componentStack && (
										<pre className="error-boundary__component-stack">
											{errorInfo.componentStack}
										</pre>
									)}
								</div>
							</details>
						)}

						<div className="error-boundary__actions">
							<button
								className="error-boundary__retry-btn"
								onClick={this.handleRetry}
								type="button"
							>
								<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
									<path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
								</svg>
								Try Again
							</button>
							<button
								className="error-boundary__home-btn"
								onClick={() => window.location.href = '/'}
								type="button"
							>
								<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
									<path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
								</svg>
								Go Home
							</button>
						</div>
					</div>
				</div>
			)
		}

		return children
	}
}

ErrorBoundary.propTypes = {
	children: PropTypes.node.isRequired,
	fallback: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
	onError: PropTypes.func,
	onRetry: PropTypes.func,
	title: PropTypes.string,
	message: PropTypes.string,
	showDetails: PropTypes.bool
}

export default ErrorBoundary
